import { Controller } from 'stimulus'

/**
 * EmbeddedIframeController
 *
 * Handles iframe resizing and theme synchronization with parent page.
 * Used when embedding scheduling links, order forms and other iframes in builder pages.
 */
export default class extends Controller {
  static values = {
    extraPadding: { type: Number, default: 0 },
    // False when no parent sizes us from the height we post — see the gate in initialize().
    notifyResize: { type: Boolean, default: true },
  }

  initialize() {
    if (window.self !== window.parent) {
      // Only pay for this where a parent is listening: each notification reads `scrollHeight`,
      // forcing a full layout of this document, and the ResizeObserver repeats that on every
      // change — so skip the whole setup, not just the callback. The receiving half of the
      // handshake is IframeComponent's `auto_resize`. It is server-rendered because this runs
      // on `<body>` before the document is parsed, so a DOM probe for the context reads false.
      if (this.notifyResizeValue) {
        this.notifyParentOfResize()

        if (window.ResizeObserver) {
          const resizeObserver = new ResizeObserver(this.notifyParentOfResize)
          resizeObserver.observe(document.body)
        }
      }

      // Set up theme listening
      this.handleParentMessage = this.handleParentMessage.bind(this)
      window.addEventListener('message', this.handleParentMessage)

      // Request theme from parent
      this.requestThemeFromParent()
    }
  }

  notifyParentOfResize = () => {
    const height = Math.max(
      document.documentElement.scrollHeight,
      this.element.scrollHeight
    )

    if (height === this.lastNotifiedHeight) return
    this.lastNotifiedHeight = height

    window.parent.postMessage(
      {
        purpose: 'simplero_resize',
        height: height + this.extraPaddingValue,
      },
      '*'
    )
  }

  handleParentMessage(event) {
    if (event.data.type === 'setTheme') {
      this.applyTheme(event.data.theme, event.data.syncBackground)
    }
  }

  applyTheme(theme, syncBackground) {
    // Every embed (builder element, "Embed code" widget, this admin modal) requests and receives
    // a real setTheme message, so its presence alone doesn't mean the parent wants us visually
    // merged into it — syncBackground is the parent explicitly saying "I'm actively mirroring my
    // own live theme into you" (IframeComponent's `theme: :current`, e.g. the admin kickoff-call
    // modal). Only then do we mark <html> so CSS paints an opaque themed background instead of
    // staying transparent — everyone else may be embedded on a page whose own background differs
    // (a builder element on a custom-background section) and must let it show through.
    if (syncBackground) document.documentElement.classList.add('theme-synced')
    document.body.classList.toggle('dark', theme === 'dark')
  }

  requestThemeFromParent() {
    try {
      window.parent.postMessage(
        {
          purpose: 'request_theme',
        },
        '*'
      )
    } catch (e) {
      console.log('Could not request theme from parent:', e)
    }
  }

  disconnect() {
    if (this.handleParentMessage) {
      window.removeEventListener('message', this.handleParentMessage)
    }
  }
}
